import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { endOfMonth, startOfMonth } from 'date-fns';
import { CalendarDays, Plus } from 'lucide-react';
import { useEvents } from '../../hooks/useEvents';
import { useAuth } from '../../hooks/useAuth';
import { formatDate } from '../../utils/formatters';
import MonthGrid from '../../components/calendar/MonthGrid';
import EventDetailsDialog from '../../components/calendar/EventDetailsDialog';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';

const Calendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const { isAdmin } = useAuth();

  // The grid shows full weeks, so widen the range a little to cover the spill days.
  const range = useMemo(
    () => ({
      dateFrom: formatDate(startOfMonth(currentMonth), 'yyyy-MM-dd'),
      dateTo: formatDate(endOfMonth(currentMonth), 'yyyy-MM-dd'),
    }),
    [currentMonth]
  );

  // Public endpoint: only published events, no auth required
  const { data, isLoading, error, refetch } = useEvents(range);

  const events = useMemo(() => data?.data || [], [data]);

  const upcoming = useMemo(
    () => [...events].sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt)).slice(0, 5),
    [events]
  );

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-brand-brown text-brand-cream">
        <div className="container mx-auto px-4 py-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <CalendarDays className="h-8 w-8 text-brand-gold" />
                <h1 className="text-3xl font-bold">Calendário</h1>
              </div>
              <p className="mt-2 text-brand-beige">
                Acompanhe os eventos e celebrações da associação.
              </p>
            </div>

            {isAdmin() && (
              <Link to="/admin/calendario/novo">
                <Button variant="gold">
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Evento
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {isLoading && <Loading text="Carregando eventos..." />}

        {error && <ErrorMessage message={error.message} onRetry={refetch} />}

        {!isLoading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Month grid */}
            <div className="lg:col-span-2">
              <MonthGrid
                events={events}
                currentMonth={currentMonth}
                onMonthChange={setCurrentMonth}
                onEventClick={setSelectedEvent}
              />
            </div>

            {/* Upcoming events */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-fit">
              <h2 className="text-lg font-semibold text-brand-brown">Próximos eventos</h2>

              {upcoming.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">
                  Nenhum evento programado para este mês.
                </p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {upcoming.map((event) => (
                    <li key={event.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedEvent(event)}
                        className="w-full text-left rounded-lg border border-gray-200 p-3 hover:border-brand-gold transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <span
                            className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full"
                            style={{ backgroundColor: event.color || '#B47330' }}
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-gray-900">
                              {event.title}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(event.startsAt, "dd 'de' MMMM")}
                              {!event.allDay && ` · ${formatDate(event.startsAt, 'HH:mm')}`}
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      <EventDetailsDialog
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </div>
  );
};

export default Calendar;
